/**
 * Page lifecycle as the watchdog needs it: is the page visible, and was it
 * frozen, discarded or restored from the back/forward cache (after which any
 * counter delta spans time the page did not run).
 */
import type { Unsubscribe } from '../core/services';

export type PageEvent = 'visibility' | 'freeze' | 'resume' | 'restored';

export interface PageLifecycle {
  visibilityState(): DocumentVisibilityState;
  /** True when this document was discarded by the browser and reloaded. */
  wasDiscarded(): boolean;
  subscribe(listener: (event: PageEvent) => void): Unsubscribe;
}

export function createBrowserPage(): PageLifecycle {
  return {
    visibilityState: () => document.visibilityState,
    wasDiscarded: () => (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true,
    subscribe(listener) {
      const onVisibility = () => listener('visibility');
      const onFreeze = () => listener('freeze');
      const onResume = () => listener('resume');
      const onPageShow = (event: PageTransitionEvent) => {
        if (event.persisted) listener('restored');
      };
      document.addEventListener('visibilitychange', onVisibility);
      document.addEventListener('freeze', onFreeze);
      document.addEventListener('resume', onResume);
      window.addEventListener('pageshow', onPageShow);
      return () => {
        document.removeEventListener('visibilitychange', onVisibility);
        document.removeEventListener('freeze', onFreeze);
        document.removeEventListener('resume', onResume);
        window.removeEventListener('pageshow', onPageShow);
      };
    },
  };
}
